/**
 * Fetch the faces once, while there is still a connection.
 *
 * `CacheFirst` only helps for what has already been seen, and a child's phone
 * may never have loaded a face before the family drives off. So this asks for
 * every known avatar once per app start; the service worker stores them, and
 * they are there at the campsite.
 *
 * The limitation that follows, and it belongs in the README rather than being
 * quietly worked around: **a photo added on the morning of departure reaches
 * the children's phones only if they open the app once more on the home wifi.**
 * Nothing here can fix that; a phone that is not talking to the server cannot
 * be handed a picture.
 */

import { useEffect } from 'react';

interface Profile {
  avatar: string | null;
}

export function useAvatarWarmup(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !navigator.onLine) return;

    let cancelled = false;

    void (async () => {
      try {
        const response = await fetch('/api/profiles', { credentials: 'same-origin' });
        if (!response.ok) return;

        const profiles = (await response.json()) as Profile[];
        for (const profile of profiles) {
          if (cancelled || !profile.avatar) continue;
          /*
           * Sequential and unawaited-on-failure. Twelve small requests are
           * nothing, but firing them in parallel on a campsite connection is
           * how you make the first screen slow for no reason. A miss is fine:
           * the picture falls back to the emoji.
           */
          await fetch(`/avatars/${profile.avatar}`, { credentials: 'same-origin' }).catch(
            () => undefined
          );
        }
      } catch {
        // Offline, or not let in. Either way there is nothing to warm.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
